// components/AddAntifraudMethodForm.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  MenuItem,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";
import { useTypes } from "../hooks/useTypes";

const TIPOS = [{ value: 1, label: "Konduto" }];

const AMBIENTES = [
  { value: "sandbox", label: "Sandbox" },
  { value: "production", label: "Produção" },
];

/** Títulos técnicos (fallback interno) */
const ANTIFRAUD_BASE_TITLES = {
  kdt_approved: "Aprovado automaticamente",
  kdt_review: "Enviado para revisão",
  kdt_refused: "Negado automaticamente",
  kdt_not_analyzed: "Não analisado",
};

/** Texto de apoio em cada cartão de mapeamento (visão de quem configura o funil) */
const KONDUTO_MAPPING_CARD = {
  kdt_approved: {
    title: "Aprovado automaticamente",
    body: "Pedidos que a Konduto libera sozinha, sem passar pela mesa.",
  },
  kdt_review: {
    title: "Enviado para revisão",
    body: "Pedidos que ficam na fila ou na mesa até alguém decidir.",
  },
  kdt_refused: {
    title: "Negado automaticamente",
    body: "Pedidos que a Konduto não libera (negados, cancelados ou não autorizados).",
  },
  kdt_not_analyzed: {
    title: "Não analisado",
    body: "Quando não houve análise antifraude para o pedido.",
  },
};

/** Lista explícita das bases que queremos exibir */
const ANTIFRAUD_BASES = [
  "kdt_approved",
  "kdt_review",
  "kdt_refused",
  // "kdt_not_analyzed",
];

/** Base pública da API para callbacks externos (alinhado a canais / telefonia). */
const PUBLIC_WEBHOOK_API_BASE = "https://secure.xpro.me";

function getPublicWebhookApiBase() {
  const explicit = String(process.env.REACT_APP_PUBLIC_WEBHOOK_API_BASE || "").trim();
  if (explicit) return explicit.replace(/\/$/, "");
  const api = String(process.env.REACT_APP_API_BASE_URL || "").trim();
  if (api && !/localhost/i.test(api)) return api.replace(/\/$/, "");
  return PUBLIC_WEBHOOK_API_BASE;
}

const WORKSPACE_DB_MIRROR_KEY = "xproWorkspaceDatabase";

function readWorkspaceDatabaseForWebhookHint() {
  if (typeof window === "undefined") return "<workspace>";
  try {
    const raw = String(window.localStorage.getItem("company") || "").trim();
    if (!raw) return "<workspace>";
    if (/^\d+$/.test(raw)) {
      const fb = String(window.localStorage.getItem(WORKSPACE_DB_MIRROR_KEY) || "").trim();
      return fb || "<workspace>";
    }
    try {
      window.localStorage.setItem(WORKSPACE_DB_MIRROR_KEY, raw);
    } catch {
      /* ignore */
    }
    return raw;
  } catch {
    return "<workspace>";
  }
}

/**
 * Rótulos iguais ao painel Konduto (Preferências → Webhooks → por tipos).
 */
const KONDUTO_WEBHOOK_ENDPOINTS = [
  {
    path: "konduto/approv",
    kondutoEventLabel: "Aprovado automaticamente",
    detail: null,
  },
  {
    path: "konduto/denied",
    kondutoEventLabel: "Negado automaticamente",
    detail: null,
  },
  {
    path: "konduto/review",
    kondutoEventLabel: "Enviado para revisão",
    detail: null,
  },
  {
    path: "konduto/upd",
    kondutoEventLabel: "Atualizado manualmente",
    detail: "Depois que alguém altera o resultado na mesa da Konduto",
  },
];

/** Helper de título genérico (mesmo padrão do AddPaymentMethodForm) */
const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;

const AddAntifraudMethodForm = ({ method, onClose, onSubmit, isFetching }) => {
  const { userData } = useAuthContext();

  // Carrega árvore de tipos (para mapeamento de status da Konduto)
  const { data: typesData, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const rootTypes = typesData?.types || [];

  const [formData, setFormData] = useState(() => ({
    id: method?.id || undefined,
    id_usuario: userData?.id || null,
    id_usuario_ultima_alteracao: userData?.id || null,
    nome: "",
    descricao: "",
    status: 1,
    tipo: 1, // Konduto
    ambiente: "sandbox",
    api_key: "",
    api_secret: "",
    merchant_id: "",
    webhook_secret: "",
    score_threshold_aprovar: "",
    score_threshold_reprovar: "",
    timeout_ms: "",
    tentativas: "",
    custo_por_transacao: "",
    custo_mensal: "",
    json: "",

    // ===== Mapeamentos de status da Konduto → Tipo/Subtipos =====
    // APROVADO
    kdt_approved_type_id: null,
    kdt_approved_type1_id: null,
    kdt_approved_type2_id: null,
    kdt_approved_type3_id: null,
    // EM ANÁLISE
    kdt_review_type_id: null,
    kdt_review_type1_id: null,
    kdt_review_type2_id: null,
    kdt_review_type3_id: null,
    // REPROVADO
    kdt_refused_type_id: null,
    kdt_refused_type1_id: null,
    kdt_refused_type2_id: null,
    kdt_refused_type3_id: null,
    // NÃO ANALISADO
    kdt_not_analyzed_type_id: null,
    kdt_not_analyzed_type1_id: null,
    kdt_not_analyzed_type2_id: null,
    kdt_not_analyzed_type3_id: null,
  }));
  const [loadingById, setLoadingById] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isValidatingCredentials, setIsValidatingCredentials] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [credentialValidation, setCredentialValidation] = useState(null);
  const credentialValidationAnchorRef = useRef(null);
  const [kondutoCopiedPath, setKondutoCopiedPath] = useState(null);

  useEffect(() => {
    if (credentialValidation && credentialValidationAnchorRef.current) {
      credentialValidationAnchorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [credentialValidation]);

  const copyKondutoWebhookUrl = async (fullUrl, pathKey) => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setKondutoCopiedPath(pathKey);
      setTimeout(() => setKondutoCopiedPath(null), 2000);
    } catch {
      setKondutoCopiedPath(null);
    }
  };

  useEffect(() => {
    setHasUnsavedChanges(false);
    setCredentialValidation(null);
  }, [method?.id]);

  // Carregar completo ao editar
  useEffect(() => {
    const id = method?.id;
    if (!id) return;
    setLoadingById(true);
    setLoadError(null);
    setCredentialValidation(null);
    fetchData(`/antifraud-methods/${id}`, "GET")
      .then((resp) => {
        const d = resp?.data || {};
        setFormData((prev) => ({
          ...prev,
          ...d,
          // nunca reidrata segredos em claro; usa apenas flags has_*
          api_key: "",
          api_secret: "",
          webhook_secret: "",
          id,
          id_usuario: d.id_usuario ?? prev.id_usuario ?? userData?.id ?? null,
          id_usuario_ultima_alteracao:
            userData?.id ?? prev.id_usuario_ultima_alteracao ?? null,
        }));
        setHasUnsavedChanges(false);
      })
      .catch((e) =>
        setLoadError(
          e?.response?.data?.message || "Falha ao carregar método."
        )
      )
      .finally(() => setLoadingById(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method?.id]);

  const isTipo = (t) => Number(formData.tipo) === Number(t);

  const podeTestarCredenciais = isTipo(1);

  const hasStoredSecret = (field) => !!formData[`has_${field}`];
  const maskSecretField = (field) => {
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const validate = () => {
    if (!String(formData.nome).trim()) {
      return "Dê um nome para este método, assim fica fácil achar depois (ex.: Konduto loja principal).";
    }
    if (![0, 1].includes(Number(formData.status))) return "Selecione se o método está ativo ou inativo.";
    if (isTipo(1)) {
      const hasApiKey = hasStoredSecret("api_key");
      if (!String(formData.api_key).trim() && !hasApiKey) {
        return "Informe a chave secreta da loja (painel Konduto → Lojas → sua loja → aba Chaves → Chaves secretas).";
      }
    }
    return null;
  };

  const numOrNull = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);
  const intOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const normalizePayload = () => {
    const p = { ...formData };
    p.status = Number(p.status) === 1 ? 1 : 0;
    p.tipo = Number(p.tipo) || 1;
    p.ambiente = p.ambiente === "production" ? "production" : "sandbox";

    p.score_threshold_aprovar = numOrNull(p.score_threshold_aprovar);
    p.score_threshold_reprovar = numOrNull(p.score_threshold_reprovar);
    p.timeout_ms = intOrNull(p.timeout_ms);
    p.tentativas = intOrNull(p.tentativas);
    p.custo_por_transacao = numOrNull(p.custo_por_transacao);
    p.custo_mensal = numOrNull(p.custo_mensal);
    if (typeof p.json === "string") {
      const t = p.json.trim();
      p.json = t || null;
    } else if (!p.json) {
      p.json = null;
    }

    // Normaliza todos os campos *_type_id / *_type[1-3]_id para number|null
    Object.keys(p).forEach((k) => {
      if (/_type(?:[123])?_id$/.test(k)) {
        const v = p[k];
        if (v === "" || v === null || typeof v === "undefined") {
          p[k] = null;
        } else {
          const n = Number(v);
          p[k] = Number.isFinite(n) ? n : null;
        }
      }
    });

    return p;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleValidateCredentials = async () => {
    if (!podeTestarCredenciais) return;
    setIsValidatingCredentials(true);
    setCredentialValidation(null);
    setErrorMessage(null);
    try {
      const payload = normalizePayload();
      if (formData?.id) payload.id = formData.id;
      const resp = await fetchData("/antifraud-methods/validate", "POST", payload);
      const r = resp?.data;
      if (r && typeof r === "object" && "valid" in r) {
        setCredentialValidation({
          valid: !!r.valid,
          title: r.userTitle || (r.valid ? "Ok" : "Atenção"),
          detail: r.userDetail || "",
        });
      } else {
        setCredentialValidation({
          valid: false,
          title: "Não foi possível concluir o teste",
          detail: "Tente de novo em instantes ou peça ajuda à TI.",
        });
      }
    } catch (error) {
      const msg =
        (error && typeof error === "object" && error.message) ||
        error?.response?.data?.message ||
        (typeof error === "string" ? error : null) ||
        "Não foi possível testar agora. Verifique os campos obrigatórios e tente de novo.";
      setCredentialValidation({
        valid: false,
        title: "Não foi possível testar",
        detail: msg,
      });
    } finally {
      setIsValidatingCredentials(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const err = validate();
    if (err) {
      setErrorMessage(err);
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit(normalizePayload());
      setHasUnsavedChanges(false);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Erro ao salvar antifraude."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contentLoading = loadingById && formData?.id;

  /** ======= Helpers de cascata de tipos para status da Konduto ======= */
  const getCascadeLevels = (base) => {
    const rootVal = formData[`${base}_type_id`];
    const root = rootTypes.find(
      (t) => Number(t.id) === Number(rootVal)
    );
    const l1 = Array.isArray(root?.children) ? root.children : [];

    const sub1Val = formData[`${base}_type1_id`];
    const n1 = l1.find((s) => Number(s.id) === Number(sub1Val));
    const l2 = Array.isArray(n1?.children) ? n1.children : [];

    const sub2Val = formData[`${base}_type2_id`];
    const n2 = l2.find((u) => Number(u.id) === Number(sub2Val));
    const l3 = Array.isArray(n2?.children) ? n2.children : [];

    return { l1, l2, l3 };
  };

  const handleCascadeChange = (base, level, val) => {
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => {
      const next = { ...prev };
      if (level === 0) {
        next[`${base}_type_id`] = val === "" ? null : val;
        next[`${base}_type1_id`] = null;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 1) {
        next[`${base}_type1_id`] = val === "" ? null : val;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 2) {
        next[`${base}_type2_id`] = val === "" ? null : val;
        next[`${base}_type3_id`] = null;
      }
      if (level === 3) {
        next[`${base}_type3_id`] = val === "" ? null : val;
      }
      return next;
    });
  };

  const renderCascade = (base) => {
    const copy = KONDUTO_MAPPING_CARD[base];
    const titulo = copy?.title ?? ANTIFRAUD_BASE_TITLES[base] ?? base;
    const subtitulo = copy?.body;
    const { l1, l2, l3 } = getCascadeLevels(base);

    return (
      <Grid item xs={12} key={base}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            height: "100%",
            borderColor: "divider",
            bgcolor: (theme) =>
              theme.palette.mode === "light" ? theme.palette.grey[50] : "action.hover",
          }}
        >
          <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {titulo}
            </Typography>
            {subtitulo && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                {subtitulo}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Tipo"
                  value={formData[`${base}_type_id`] ?? ""}
                  onChange={(e) => handleCascadeChange(base, 0, e.target.value)}
                  disabled={loadingTypes}
                  helperText="Etapa principal do pedido no Xpro"
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  {!loadingTypes &&
                    rootTypes.map((t) => (
                      <MenuItem key={`${base}-root-${t.id}`} value={t.id}>
                        {titleOf(t)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              {l1.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Subtipo 1"
                    value={formData[`${base}_type1_id`] ?? ""}
                    onChange={(e) => handleCascadeChange(base, 1, e.target.value)}
                    helperText="Opcional"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {l1.map((s) => (
                      <MenuItem key={`${base}-l1-${s.id}`} value={s.id}>
                        {titleOf(s)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {l2.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Subtipo 2"
                    value={formData[`${base}_type2_id`] ?? ""}
                    onChange={(e) => handleCascadeChange(base, 2, e.target.value)}
                    helperText="Opcional"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {l2.map((u) => (
                      <MenuItem key={`${base}-l2-${u.id}`} value={u.id}>
                        {titleOf(u)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {l3.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Subtipo 3"
                    value={formData[`${base}_type3_id`] ?? ""}
                    onChange={(e) => handleCascadeChange(base, 3, e.target.value)}
                    helperText="Opcional"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {l3.map((v) => (
                      <MenuItem key={`${base}-l3-${v.id}`} value={v.id}>
                        {titleOf(v)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  /** Bases que realmente existem no formData (mantém flexível, igual payment) */
  const cascadeBases = useMemo(
    () =>
      ANTIFRAUD_BASES.filter((b) =>
        Object.prototype.hasOwnProperty.call(formData, `${b}_type_id`)
      ),
    [formData]
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3 },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 2.5 }}>
        <Typography variant="h6" component="h2">
          {formData?.id ? "Editar método de antifraude" : "Novo método de antifraude"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isTipo(1) ? (
            <>
              Use a mesma loja do painel Konduto (<strong>Lojas</strong> → engrenagem da loja). Cole as URLs em{" "}
              <strong>Preferências</strong> → <strong>Webhooks</strong> e, abaixo, defina como cada pedido aparece no
              Xpro.
            </>
          ) : (
            <>Preencha os dados do provedor e salve.</>
          )}
        </Typography>
      </Stack>

      {loadError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}
      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {contentLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} spacing={2}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary">
            Carregando configuração…
          </Typography>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Identificação
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nome para a equipe reconhecer este cadastro. O ambiente deve ser o mesmo da loja na Konduto (teste
                    ou produção).
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Nome do método"
                        name="nome"
                        value={formData.nome || ""}
                        onChange={handleChange}
                        fullWidth
                        required
                        inputProps={{ maxLength: 120 }}
                        placeholder="Ex.: Konduto — loja principal"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Situação"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        fullWidth
                        select
                        required
                        helperText="Inativo pausa o uso deste método"
                      >
                        <MenuItem value={1}>Ativo</MenuItem>
                        <MenuItem value={0}>Inativo</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Provedor"
                        name="tipo"
                        value={formData.tipo}
                        onChange={handleChange}
                        fullWidth
                        select
                        required
                      >
                        {TIPOS.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={isTipo(1) ? "Ambiente Konduto" : "Ambiente"}
                        name="ambiente"
                        value={formData.ambiente}
                        onChange={handleChange}
                        fullWidth
                        select
                        helperText={
                          isTipo(1)
                            ? "Use produção quando for ao ar de verdade"
                            : "Sandbox ou produção, conforme o provedor"
                        }
                      >
                        {AMBIENTES.map((a) => (
                          <MenuItem key={a.value} value={a.value}>
                            {a.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Observações (opcional)"
                        name="descricao"
                        value={formData.descricao || ""}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Notas internas: quem mantém, loja física, etc."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {isTipo(1) && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Chaves
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No painel Konduto: <strong>Lojas</strong> → sua loja → aba <strong>Chaves</strong>. Copie o valor
                      da lista <strong>Chaves secretas</strong> (revele com o ícone de olho, se estiver oculto). Não
                      envie essa chave por e-mail ou grupos abertos.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        {(() => {
                          const hasApiKey = hasStoredSecret("api_key");
                          const isMasked = hasApiKey && !formData.api_key;
                          return (
                            <TextField
                              label="Chave secreta"
                              name="api_key"
                              value={isMasked ? "••••••••" : formData.api_key || ""}
                              onChange={isMasked ? undefined : handleChange}
                              fullWidth
                              type="password"
                              autoComplete="new-password"
                              required={!hasApiKey}
                              helperText={
                                isMasked
                                  ? "Toque no campo para colar uma chave nova, se precisar trocar."
                                  : !formData.api_key && hasApiKey
                                  ? "Deixe em branco para manter a chave já salva; preencha só se for alterar."
                                  : "Cole o mesmo valor exibido em Chaves secretas na Konduto."
                              }
                              InputProps={isMasked ? { readOnly: true } : undefined}
                              onClick={isMasked ? () => maskSecretField("api_key") : undefined}
                              onFocus={isMasked ? () => maskSecretField("api_key") : undefined}
                            />
                          );
                        })()}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="ID da loja (opcional)"
                          name="merchant_id"
                          value={formData.merchant_id || ""}
                          onChange={handleChange}
                          fullWidth
                          helperText="Só se o suporte da Konduto pedir um identificador extra da loja."
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {(() => {
                          const hasWebhook = hasStoredSecret("webhook_secret");
                          const isMasked = hasWebhook && !formData.webhook_secret;
                          return (
                            <TextField
                              label="Segredo extra de webhook (opcional)"
                              name="webhook_secret"
                              value={isMasked ? "••••••••" : formData.webhook_secret || ""}
                              onChange={isMasked ? undefined : handleChange}
                              fullWidth
                              type="password"
                              autoComplete="new-password"
                              helperText={
                                isMasked
                                  ? "Toque aqui para definir um segredo novo."
                                  : !formData.webhook_secret && hasWebhook
                                  ? "Já existe um valor salvo. Preencha apenas para substituir."
                                  : "Na maioria das vezes pode ficar em branco. Use só se a sua operação pedir este campo."
                              }
                              InputProps={isMasked ? { readOnly: true } : undefined}
                              onClick={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                              onFocus={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                            />
                          );
                        })()}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Chave pública e outros (JSON opcional)"
                          name="json"
                          value={formData.json || ""}
                          onChange={handleChange}
                          fullWidth
                          multiline
                          minRows={2}
                          placeholder='{"kondutoPublicId":"PDA…"}'
                          helperText="Deixe em branco salvo orientação da equipe de TI da loja."
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {isTipo(1) && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Webhooks
                    </Typography>
                    <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Na Konduto: <strong>Preferências</strong> → <strong>Webhooks</strong>. Ative{" "}
                      <strong>Mostrar webhooks por tipos</strong> para ver quatro campos. Em cada um, use{" "}
                      <strong>Copiar</strong> na linha correspondente e cole no <strong>Insira uma URL</strong> da
                      mesma situação (mesmo nome na esquerda e na Konduto). Se só aparecer um campo, troque para{" "}
                      <strong>Mostrar webhooks por tipos</strong> antes de colar.
                    </Typography>
                    {(() => {
                      const company = readWorkspaceDatabaseForWebhookHint();
                      const apiBase = getPublicWebhookApiBase();
                      const companyNote =
                        company === "<workspace>"
                          ? "Selecione a empresa no topo do Xpro para gerar as URLs com o nome certo da loja."
                          : null;
                      return (
                        <>
                          {companyNote && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              {companyNote}
                            </Alert>
                          )}
                          {company !== "<workspace>" && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              As URLs já levam em conta a empresa em que você está no Xpro agora. Se precisar de outra
                              loja, troque a empresa no topo e copie de novo.
                            </Typography>
                          )}
                          <TableContainer sx={{ borderRadius: 1, border: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ tableLayout: "fixed" }}>
                              <TableHead>
                                <TableRow sx={{ bgcolor: "action.hover" }}>
                                  <TableCell width="32%">Evento (rótulo na Konduto)</TableCell>
                                  <TableCell width="56%">Cole no campo &quot;Insira uma URL&quot;</TableCell>
                                  <TableCell width="12%" align="right">
                                    Ação
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {KONDUTO_WEBHOOK_ENDPOINTS.map((row) => {
                                  const qs =
                                    company === "<workspace>"
                                      ? "company=<workspace>"
                                      : `company=${encodeURIComponent(company)}`;
                                  const fullUrl = `${apiBase}/${row.path}?${qs}`;
                                  const pathKey = row.path;
                                  return (
                                    <TableRow key={pathKey} hover>
                                      <TableCell sx={{ verticalAlign: "top" }}>
                                        <Typography variant="body2" fontWeight={600}>
                                          {row.kondutoEventLabel}
                                        </Typography>
                                        {row.detail ? (
                                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                            {row.detail}
                                          </Typography>
                                        ) : null}
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: "top", wordBreak: "break-all" }}>
                                        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                                          {fullUrl}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" sx={{ verticalAlign: "top" }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={kondutoCopiedPath === pathKey ? <CheckIcon /> : <ContentCopyIcon />}
                                          onClick={() => copyKondutoWebhookUrl(fullUrl, pathKey)}
                                          sx={{ whiteSpace: "nowrap" }}
                                        >
                                          {kondutoCopiedPath === pathKey ? "Copiado" : "Copiar"}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {isTipo(2) && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      ClearSale
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="API Key"
                          name="api_key"
                          value={formData.api_key || ""}
                          onChange={handleChange}
                          fullWidth
                          type="password"
                          autoComplete="new-password"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="API Secret"
                          name="api_secret"
                          value={formData.api_secret || ""}
                          onChange={handleChange}
                          fullWidth
                          type="password"
                          autoComplete="new-password"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {isTipo(3) && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      FControl
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="API Key"
                          name="api_key"
                          value={formData.api_key || ""}
                          onChange={handleChange}
                          fullWidth
                          type="password"
                          autoComplete="new-password"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Merchant ID"
                          name="merchant_id"
                          value={formData.merchant_id || ""}
                          onChange={handleChange}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {isTipo(9) && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Integração customizada
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="API Key"
                          name="api_key"
                          value={formData.api_key || ""}
                          onChange={handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="API Secret"
                          name="api_secret"
                          value={formData.api_secret || ""}
                          onChange={handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Config JSON (opcional)"
                          name="json"
                          value={formData.json || ""}
                          onChange={handleChange}
                          fullWidth
                          multiline
                          minRows={3}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Parâmetros opcionais
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pode ficar em branco na maior parte dos casos. Preencha só se a sua operação ou TI usarem esses
                    números no dia a dia.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Score mínimo para aprovar"
                        name="score_threshold_aprovar"
                        value={formData.score_threshold_aprovar ?? ""}
                        onChange={handleChange}
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.01" }}
                        helperText="Só se a operação combinar limite de nota com o antifraude"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Score máximo antes de reprovar"
                        name="score_threshold_reprovar"
                        value={formData.score_threshold_reprovar ?? ""}
                        onChange={handleChange}
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.01" }}
                        helperText="Só se a operação usar limite inferior de nota"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Tempo máximo de espera (ms)"
                        name="timeout_ms"
                        value={formData.timeout_ms ?? ""}
                        onChange={handleChange}
                        fullWidth
                        type="number"
                        helperText="Só preencha se TI pedir (tempo máximo de espera)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Tentativas em caso de falha"
                        name="tentativas"
                        value={formData.tentativas ?? ""}
                        onChange={handleChange}
                        fullWidth
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Custo por transação (R$)"
                        name="custo_por_transacao"
                        value={formData.custo_por_transacao ?? ""}
                        onChange={handleChange}
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.01" }}
                        helperText="Para controle interno / financeiro"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Custo mensal estimado (R$)"
                        name="custo_mensal"
                        value={formData.custo_mensal ?? ""}
                        onChange={handleChange}
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.01" }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {cascadeBases.length > 0 && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "primary.light" }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Situação do pedido no Xpro
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 800 }}>
                      Para cada resultado da Konduto (mesmos nomes dos webhooks), diga em qual tipo e subtipos o
                      pedido deve aparecer no Xpro. Isso organiza o pipeline e os relatórios.
                    </Typography>
                    <Grid container spacing={2}>
                      {cascadeBases.map((base) => renderCascade(base))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {credentialValidation && (
            <Box
              ref={credentialValidationAnchorRef}
              sx={{ mt: 3, width: "100%" }}
              id="antifraud-method-credential-validation"
            >
              <Alert severity={credentialValidation.valid ? "success" : "warning"}>
                <Typography variant="subtitle2" component="span" display="block" gutterBottom>
                  {credentialValidation.title}
                </Typography>
                <Typography variant="body2">{credentialValidation.detail}</Typography>
              </Alert>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 2,
              mt: credentialValidation ? 2 : 3,
            }}
          >
            {podeTestarCredenciais && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={handleValidateCredentials}
                disabled={
                  hasUnsavedChanges ||
                  isValidatingCredentials ||
                  isSubmitting ||
                  isFetching ||
                  contentLoading
                }
                sx={{ mr: "auto" }}
              >
                {isValidatingCredentials ? <CircularProgress size={20} /> : "Conferir conexão com a Konduto"}
              </Button>
            )}
            {podeTestarCredenciais && hasUnsavedChanges && (
              <Typography variant="caption" sx={{ mr: "auto", alignSelf: "center", opacity: 0.8, maxWidth: 280 }}>
                Salve o formulário antes de testar a conexão.
              </Typography>
            )}
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting || isFetching}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || isFetching}
            >
              {isFetching || isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Salvar"
              )}
            </Button>
          </Box>
        </form>
      )}
    </Paper>
  );
};

export default AddAntifraudMethodForm;
